import { useState } from "react";
import useSWR from "swr";

import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ToastProvider";
import { post } from "@/lib/api";

const fetcher = (url: string) => fetch(url, { credentials: "include" }).then((res) => res.json());

export function CreatorPayoutPanel() {
  const { push } = useToast();
  const [form, setForm] = useState({ collaborator: "", platform: "instagram", revenue: 0, split: 50, status: "pending" });
  const [selectedId, setSelectedId] = useState<string>("");
  const { data, mutate } = useSWR(selectedId ? `/marketing/collabs/payouts/${selectedId}` : null, fetcher);

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreate = async () => {
    if (!form.collaborator.trim()) {
      push({ title: "Collaborator required", message: "Add a collaborator name first.", variant: "destructive" });
      return;
    }
    try {
      const payload = {
        collaborator: form.collaborator,
        platform: form.platform,
        revenue: Number(form.revenue) || 0,
        split_percentage: Number(form.split) || 0,
        status: form.status,
      };
      const response = await post("/marketing/collabs/payouts", payload);
      push({ title: "Payout recorded", message: `Logged ${payload.collaborator}.` });
      setSelectedId(String(response.id));
      await mutate();
    } catch (error) {
      push({
        title: "Unable to save payout",
        message: error instanceof Error ? error.message : "Try again later.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="space-y-3 border-border/70 bg-background/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-foreground">Creator payouts</p>
          <p className="text-xs text-muted-foreground">Track rev share with collaborators.</p>
        </div>
        <Input
          value={selectedId}
          onChange={(event) => setSelectedId(event.target.value)}
          placeholder="Lookup payout ID"
          className="w-40 bg-background/50 text-foreground"
        />
      </div>

      <div className="grid gap-2 md:grid-cols-2">
        <Input
          value={form.collaborator}
          onChange={(event) => handleChange("collaborator", event.target.value)}
          placeholder="Collaborator"
          className="bg-background/40 text-foreground"
        />
        <Input
          value={form.platform}
          onChange={(event) => handleChange("platform", event.target.value)}
          placeholder="Platform (instagram/tiktok/etc)"
          className="bg-background/40 text-foreground"
        />
        <Input
          type="number"
          value={form.revenue}
          onChange={(event) => handleChange("revenue", event.target.value)}
          placeholder="Revenue"
          className="bg-background/40 text-foreground"
        />
        <Input
          type="number"
          value={form.split}
          onChange={(event) => handleChange("split", event.target.value)}
          placeholder="Split %"
          className="bg-background/40 text-foreground"
        />
        <Input
          value={form.status}
          onChange={(event) => handleChange("status", event.target.value)}
          placeholder="Status"
          className="bg-background/40 text-foreground"
        />
      </div>

      <Button onClick={() => void handleCreate()}>Record payout</Button>

      {data ? (
        <div className="rounded-xl border border-border/70 bg-background/40 p-3 text-sm text-foreground/80">
          <p className="font-semibold text-foreground">Payout #{data.id}</p>
          <p>{data.collaborator} · {data.platform}</p>
          <p>${data.revenue} · {data.split_percentage}% split</p>
          <p>Status: {data.status}</p>
          <p className="text-xs text-muted-foreground">Created {data.created_at}</p>
        </div>
      ) : selectedId ? (
        <p className="text-xs text-muted-foreground">No payout found for ID {selectedId}.</p>
      ) : null}
    </Card>
  );
}
